<template>
  <v-container fluid class="servicios-reconocimiento-root">
    <v-card tile>
      <div class="pl-6">
        <p class="text-body-1 font-weight-regular pt-4">
          Servicios y Estado de Evaluaciones — Canal Web Reconocimiento
        </p>
        <p
          class="text-caption text--secondary font-weight-bold mt-n3 pb-4"
          v-if="selectedFecha && selectedFecha[0]"
        >
          <v-icon color="black">mdi-calendar-range</v-icon>
          del {{ selectedFecha[0] }} al {{ selectedFecha[1] }}
        </p>
      </div>
    </v-card>

    <!-- Selector de rango de fechas -->
    <v-row class="mt-2">
      <v-col cols="12" sm="4" md="3">
        <v-menu
          ref="fechaDialog"
          v-model="modalFechas"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="selectedFechaTexto"
              readonly
              dense
              hide-details
              outlined
              label="Rango de fechas"
              prepend-icon="mdi-calendar"
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            locale="es"
            width="240"
            no-title
            scrollable
            v-model="dialogVarSelectedFecha"
            range
            :max="disabledFecha"
            color="primary"
            header-color="primary"
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="modalFechas = false"
              >Cancelar</v-btn
            >
            <v-btn text color="primary" @click="fnAplicarRango">OK</v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" sm="8" md="5">
        <v-text-field
          v-model="busqueda"
          dense
          hide-details
          outlined
          label="Buscar por DPI o nombre"
          prepend-icon="mdi-magnify"
          clearable
        ></v-text-field>
      </v-col>
    </v-row>

    <v-alert type="info" outlined dense class="mt-2">
      Este reporte muestra en qué paso del proceso está cada evaluación y qué
      servicios de verificación se han usado hasta el momento.
    </v-alert>

    <!-- KPIs -->
    <v-row v-if="!cargando" class="mt-2">
      <v-col cols="6" sm="3" md="2">
        <v-card outlined class="pa-3 fill-height">
          <div class="text-caption text--secondary">
            Total de evaluaciones
            <v-tooltip bottom max-width="260">
              <template v-slot:activator="{ on, attrs }">
                <v-icon x-small v-bind="attrs" v-on="on"
                  >mdi-information-outline</v-icon
                >
              </template>
              <span
                >Cantidad de evaluaciones creadas en el rango de fechas
                seleccionado.</span
              >
            </v-tooltip>
          </div>
          <div class="text-h5 font-weight-medium">
            {{ fnFormatNumero(resumen.totalGestiones) }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3" md="2" v-for="paso in pasosResumen" :key="paso.clave">
        <v-card outlined class="pa-3 fill-height">
          <div class="text-caption text--secondary">
            {{ paso.titulo }}
            <v-tooltip bottom max-width="260">
              <template v-slot:activator="{ on, attrs }">
                <v-icon x-small v-bind="attrs" v-on="on"
                  >mdi-information-outline</v-icon
                >
              </template>
              <span>{{ paso.descripcion }}</span>
            </v-tooltip>
          </div>
          <div class="text-h5 font-weight-medium">
            {{ fnFormatNumero(resumen.porPaso[paso.clave]) }}
          </div>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" class="text-center pa-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>

    <!-- Gráfica de distribución por paso -->
    <v-row class="mt-2">
      <v-col cols="12" md="6">
        <v-card outlined class="pa-3">
          <div class="text-subtitle-2 mb-2">
            Distribución de evaluaciones por paso actual
          </div>
          <canvas ref="canvasDistribucion" height="220"></canvas>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabla por gestión -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-subtitle-1"
            >Detalle por gestión (periodo seleccionado)</v-card-title
          >
          <v-data-table
            dense
            :headers="headers"
            :items="filasFiltradas"
            :loading="cargando"
            class="elevation-1"
          >
            <template v-slot:[`header.pasoActual`]="{ header }">
              {{ header.text }}
              <v-tooltip bottom max-width="260">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small v-bind="attrs" v-on="on"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <span
                  >En qué parte del proceso se quedó la persona hasta el
                  momento de esta consulta.</span
                >
              </v-tooltip>
            </template>
            <template v-slot:[`header.serviciosUsados`]="{ header }">
              {{ header.text }}
              <v-tooltip bottom max-width="260">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small v-bind="attrs" v-on="on"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <span
                  >Verificaciones que ya se le hicieron a esta evaluación:
                  revisión del documento de identificación, comparación de
                  rostro, código de verificación (OTP) y/o consulta a buró de
                  crédito.</span
                >
              </v-tooltip>
            </template>
            <template v-slot:[`item.id`]="{ item }">
              <router-link :to="`/detalle-reconocimiento-facial/${item.id}`">
                {{ item.id }}
              </router-link>
            </template>
            <template v-slot:[`item.createdAt`]="{ item }">
              {{ fnFormatFecha(item.createdAt) }}
            </template>
            <template v-slot:[`item.pasoActual`]="{ item }">
              <v-chip x-small :color="fnColorPaso(item.pasoClave)" dark>
                {{ item.pasoActual }}
              </v-chip>
            </template>
            <template v-slot:[`item.serviciosUsados`]="{ item }">
              <span v-if="!item.serviciosUsados.length" class="text--disabled"
                >— sin registro —</span
              >
              <v-chip
                v-for="servicio in item.serviciosUsados"
                :key="servicio"
                small
                outlined
                class="mr-1 mb-1"
              >
                {{ servicio }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment';
require('moment/locale/es.js');
import axios from 'axios';
import {
  Chart,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';

Chart.register(BarElement, BarController, CategoryScale, LinearScale, Tooltip, Legend);

// Paleta validada (skill dataviz — references/palette.md), sin modo oscuro: este panel no tiene tema oscuro.
const COLOR_BARRA = '#2a78d6';
const COLOR_MUTED = '#898781';
const COLOR_GRID = '#e1e0d9';

const PASOS_RESUMEN = [
  {
    clave: 'step1',
    titulo: 'En Step 1',
    descripcion:
      'Evaluaciones que llegaron al menos hasta el Step 1: ingresar datos de contacto (teléfono y correo).',
  },
  {
    clave: 'step2',
    titulo: 'En Step 2',
    descripcion:
      'Evaluaciones que ya verificaron su identidad con el código (OTP) enviado por SMS o correo.',
  },
  {
    clave: 'step3',
    titulo: 'En Step 3',
    descripcion:
      'Evaluaciones que ya registraron su domicilio y subieron fotos de fachada y recibo de servicios.',
  },
  {
    clave: 'step4',
    titulo: 'En Step 4',
    descripcion:
      'Evaluaciones que llegaron al último paso: foto de identificación, verificación facial y firma.',
  },
  {
    clave: 'completo',
    titulo: 'Completadas',
    descripcion:
      'Evaluaciones que terminaron todo el proceso y ya tienen un resultado (aprobado, rechazado o en revisión).',
  },
  {
    clave: 'nd',
    titulo: 'N/D',
    descripcion: 'Evaluaciones sin ningún paso registrado todavía.',
  },
];

const COLOR_POR_PASO = {
  step1: 'blue-grey',
  step2: 'blue-grey',
  step3: 'blue-grey',
  step4: 'orange darken-1',
  completo: 'green',
  nd: 'grey',
};

export default {
  name: 'serviciosEvaluacionReconocimiento',
  data() {
    return {
      cargando: true,
      busqueda: '',

      modalFechas: false,
      dialogVarSelectedFecha: [
        moment().startOf('month').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD'),
      ],
      selectedFecha: [
        moment().startOf('month').format('DD-MM-YYYY'),
        moment().format('DD-MM-YYYY'),
      ],
      disabledFecha: moment().format('YYYY-MM-DD'),

      resumen: {
        totalGestiones: 0,
        porPaso: { step1: 0, step2: 0, step3: 0, step4: 0, completo: 0, nd: 0 },
      },
      filas: [],

      pasosResumen: PASOS_RESUMEN,

      headers: [
        { text: 'ID', value: 'id' },
        { text: 'DPI', value: 'dpi' },
        { text: 'Nombre', value: 'deudor_nombre_completo' },
        { text: 'País', value: 'pais' },
        { text: 'Fecha', value: 'createdAt' },
        { text: 'Paso actual', value: 'pasoActual' },
        { text: 'Servicios usados', value: 'serviciosUsados', sortable: false },
      ],

      chartDistribucion: null,
    };
  },

  computed: {
    feathersServer() {
      return process.env.VUE_APP_FEATHERS_SERVER;
    },
    selectedFechaTexto() {
      return this.selectedFecha && this.selectedFecha[0]
        ? `${this.selectedFecha[0]} — ${this.selectedFecha[1]}`
        : '';
    },
    filasFiltradas() {
      const texto = (this.busqueda || '').trim().toLowerCase();
      if (!texto) return this.filas;
      return this.filas.filter((f) =>
        (f.dpi || '').toLowerCase().includes(texto) ||
        (f.deudor_nombre_completo || '').toLowerCase().includes(texto)
      );
    },
  },

  methods: {
    fnFormatNumero(n) {
      return Number(n || 0).toLocaleString('es-GT');
    },
    fnFormatFecha(fecha) {
      return fecha ? moment(fecha).format('DD-MM-YYYY HH:mm') : 'N/D';
    },
    fnColorPaso(pasoClave) {
      return COLOR_POR_PASO[pasoClave] || 'grey';
    },
    fnAuthHeaders() {
      return {
        Authorization: 'Bearer ' + window.localStorage.getItem('feathers-jwt'),
      };
    },

    fnAplicarRango() {
      this.selectedFecha = [
        moment(this.dialogVarSelectedFecha[0]).format('DD-MM-YYYY'),
        moment(this.dialogVarSelectedFecha[1]).format('DD-MM-YYYY'),
      ];
      this.modalFechas = false;
      this.cargarReporte();
    },

    async cargarReporte() {
      this.cargando = true;
      try {
        const [start, end] = this.dialogVarSelectedFecha;
        const r = await axios.get(
          `${this.feathersServer}reconocimiento-servicios-evaluacion`,
          {
            params: { start, end },
            headers: this.fnAuthHeaders(),
            timeout: 30000,
          }
        );
        this.resumen = r.data.resumen || this.resumen;
        this.filas = r.data.filas || [];
      } catch (err) {
        console.error('reconocimiento-servicios-evaluacion: error al cargar reporte', err.message);
        this.resumen = {
          totalGestiones: 0,
          porPaso: { step1: 0, step2: 0, step3: 0, step4: 0, completo: 0, nd: 0 },
        };
        this.filas = [];
      } finally {
        this.cargando = false;
        this.$nextTick(() => this.renderGraficaDistribucion());
      }
    },

    renderGraficaDistribucion() {
      if (this.chartDistribucion) {
        this.chartDistribucion.destroy();
        this.chartDistribucion = null;
      }
      const canvas = this.$refs.canvasDistribucion;
      if (!canvas) return;

      const porPaso = this.resumen.porPaso || {};
      this.chartDistribucion = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels: this.pasosResumen.map((p) => p.titulo),
          datasets: [
            {
              data: this.pasosResumen.map((p) => porPaso[p.clave] || 0),
              backgroundColor: COLOR_BARRA,
              borderRadius: 4,
              maxBarThickness: 40,
            },
          ],
        },
        options: {
          plugins: { legend: { display: false } },
          scales: {
            x: { grid: { display: false }, ticks: { color: COLOR_MUTED } },
            y: {
              beginAtZero: true,
              ticks: { precision: 0, color: COLOR_MUTED },
              grid: { color: COLOR_GRID, drawBorder: false },
            },
          },
        },
      });
    },
  },

  mounted() {
    this.cargarReporte();
  },

  beforeDestroy() {
    if (this.chartDistribucion) this.chartDistribucion.destroy();
  },
};
</script>

<style scoped>
.servicios-reconocimiento-root {
  --surface-1: #fcfcfb;
  --text-secondary: #52514e;
  --text-muted: #898781;
}
</style>
